/**
 * Event publishing / subscribe module.
 * Just a simple observer pattern.
 * @version 1
 */

type ListenerType = (data: any) => void

export interface SubscriptionType {
    unsubscribe(): void
}

interface TopicType {
    order: number
    listener: ListenerType
}

interface TopicTypes {
    [key: string]: TopicType[]
}

export var mEvent = (() => {
    const topics: TopicTypes = {}

    return {
        /**
         * Subscribes onto some event.
         *
         * @param topic The name of the event.
         * @param listener A callback function to call during the event.
         * @param order A number in a queue of event handlers to run callback in ordered manner.
         * @returns {{unsubscribe: unsubscribe}} The function to remove this subscription.
         * @example
         * const sub01 = event.subscribe('rapture', () => {a}, 1)
         * ...
         * sub01.unsubscribe()
         */
        subscribe: (
            topic: string,
            listener: ListenerType,
            order?: number
        ): SubscriptionType => {
            if (!topics[topic]) topics[topic] = []

            // order handling stuff
            const value = {
                order: order || 0,
                listener: listener,
            }
            topics[topic].push(value)
            topics[topic].sort(
                (a: TopicType, b: TopicType) => a.order - b.order
            )
            const index = topics[topic].indexOf(value)

            return {
                unsubscribe: () => {
                    topics[topic].splice(index, 1)
                },
            }
        },

        /**
         * Dispatch some event for handling.
         *
         * @param topic The name of the event.
         * @param data Additional data for the event handling.
         * Because of compatibility we have to use a dumb obj wrapper {a: a, b: b} for params instead of (topic, ...data).
         * @example
         * event.dispatch('rapture', {time: now()})
         */
        dispatch: (topic: string, data?: any) => {
            if (!topics[topic] || topics[topic].length < 1) {
                return
            }

            topics[topic].forEach((listener: TopicType) => {
                listener.listener(data !== undefined ? data : {})
            })
        },
    }
})()

enum EventType {
    ALERT_LOW_BANDWIDTH = 'alertLowBandwidth',
    AUDIO_KBPS_UPDATED = 'AUDIO_KBPS_UPDATED',
    AUDIO_PACKETS_LOST = 'AUDIO_PACKETS_LOST',
    CHANGE_BITRATE = 'changeBitrate',
    CONNECTION_CLOSED = 'connectionClosed',
    CONNECTION_OPEN = 'connectionOpen',
    CONNECTION_READY = 'connectionReady',
    CURSOR_CHANGE_HIDE = 'cursorChangeHide',
    CURSOR_CHANGE_MODE = 'cursorChangeMode',
    CURSOR_CHANGE_POSITION = 'cursorChangePosition',
    CURSOR_CHANGE_SHAPE = 'cursorChangeShape',
    DESKTOP_FULLSCREEN_CHANGED = 'DESKTOP_FULLSCREEN_CHANGED',
    FILE_PROGRESS = 'FILE_PROGRESS',
    FILE_TRANSFERRED = 'FILE_TRANSFERRED',
    INBOUND_RTP_REPORT = 'inboundReport',
    INPUT_CHANNEL_READY = 'inputChannelReady',
    KBPS_UPDATED = 'KBPS_UPDATED',
    LATENCY_CHECK_REQUESTED = 'LATENCY_CHECK_REQUESTED',
    MEDIA_STREAM_CANDIDATE_ADD = 'mediaStreamCandidateAdd',
    MEDIA_STREAM_CANDIDATE_FLUSH = 'mediaStreamCandidateFlush',
    MEDIA_STREAM_INITIALIZED = 'mediaStreamInitialized',
    MEDIA_STREAM_SDP_AVAILABLE = 'mediaStreamSdpAvailable',
    NETWORK_LATENCY_UPDATED = 'NETWORK_LATENCY_UPDATED',
    NOT_SUPPORTED = 'notSupported',
    PING_REQUEST = 'pingRequest',
    PING_RESPONSE = 'pingResponse',
    RETRY_CONNECTION = 'retryConnection',
    STATS_CONNECTION_CLOSED = 'sendingStatsOnConnectionClosed',
    STREAM_ERROR = 'streamError',
    UNSTABLE_CONNECTION = 'unstableConnection',
    USER_INTERACTION = 'userInteraction',
    VD_AGENT_CONNECTED = 'vdAgentConnected',
    VD_AGENT_DISCONNECTED = 'vdAgentDisconnected',
    WEBCAM_STREAM_ANSWER_AVAILABLE = 'WEBCAM_STREAM_ANSWER_AVAILABLE',
    WEBCAM_STREAM_CANDIDATE_ADD = 'WEBCAM_STREAM_CANDIDATE_ADD',
    WEBCAM_STREAM_CANDIDATE_FLUSH = 'WEBCAM_STREAM_CANDIDATE_FLUSH',
    WEBCAM_STREAM_CLOSE = 'WEBCAM_STREAM_CLOSE',
    WEBCAM_STREAM_READY = 'WEBCAM_STREAM_READY',
    WEBCAM_STREAM_SDP_OFFER = 'WEBCAM_STREAM_SDP_OFFER',
}

export const MEDIA_STREAM_INITIALIZED = EventType.MEDIA_STREAM_INITIALIZED
export const MEDIA_STREAM_SDP_AVAILABLE = EventType.MEDIA_STREAM_SDP_AVAILABLE
export const MEDIA_STREAM_CANDIDATE_ADD = EventType.MEDIA_STREAM_CANDIDATE_ADD
export const MEDIA_STREAM_CANDIDATE_FLUSH =
    EventType.MEDIA_STREAM_CANDIDATE_FLUSH
export const CONNECTION_READY = EventType.CONNECTION_READY
export const INPUT_CHANNEL_READY = EventType.INPUT_CHANNEL_READY
export const CONNECTION_CLOSED = EventType.CONNECTION_CLOSED
export const CONNECTION_OPEN = EventType.CONNECTION_OPEN
export const STREAM_ERROR = EventType.STREAM_ERROR
export const RETRY_CONNECTION = EventType.RETRY_CONNECTION
export const VD_AGENT_DISCONNECTED = EventType.VD_AGENT_DISCONNECTED
export const VD_AGENT_CONNECTED = EventType.VD_AGENT_CONNECTED
export const PING_REQUEST = EventType.PING_REQUEST
export const PING_RESPONSE = EventType.PING_RESPONSE
export const CURSOR_CHANGE_SHAPE = EventType.CURSOR_CHANGE_SHAPE
export const CURSOR_CHANGE_POSITION = EventType.CURSOR_CHANGE_POSITION
export const CURSOR_CHANGE_MODE = EventType.CURSOR_CHANGE_MODE
export const CURSOR_CHANGE_HIDE = EventType.CURSOR_CHANGE_HIDE
export const CHANGE_BITRATE = EventType.CHANGE_BITRATE
export const UNSTABLE_CONNECTION = EventType.UNSTABLE_CONNECTION
export const ALERT_LOW_BANDWIDTH = EventType.ALERT_LOW_BANDWIDTH
export const AUDIO_KBPS_UPDATED = EventType.AUDIO_KBPS_UPDATED
export const AUDIO_PACKETS_LOST = EventType.AUDIO_PACKETS_LOST
export const KBPS_UPDATED = EventType.KBPS_UPDATED
export const LATENCY_CHECK_REQUESTED = EventType.LATENCY_CHECK_REQUESTED
export const FILE_PROGRESS = EventType.FILE_PROGRESS
export const FILE_TRANSFERRED = EventType.FILE_TRANSFERRED
export const NETWORK_LATENCY_UPDATED = EventType.NETWORK_LATENCY_UPDATED
export const NOT_SUPPORTED = EventType.NOT_SUPPORTED
export const USER_INTERACTION = EventType.USER_INTERACTION
export const INBOUND_RTP_REPORT = EventType.INBOUND_RTP_REPORT
export const STATS_CONNECTION_CLOSED = EventType.STATS_CONNECTION_CLOSED
export const WEBCAM_STREAM_SDP_OFFER = EventType.WEBCAM_STREAM_SDP_OFFER
export const WEBCAM_STREAM_CANDIDATE_ADD = EventType.WEBCAM_STREAM_CANDIDATE_ADD
export const WEBCAM_STREAM_CANDIDATE_FLUSH =
    EventType.WEBCAM_STREAM_CANDIDATE_FLUSH
export const WEBCAM_STREAM_READY = EventType.WEBCAM_STREAM_READY
export const WEBCAM_STREAM_ANSWER_AVAILABLE =
    EventType.WEBCAM_STREAM_ANSWER_AVAILABLE
export const WEBCAM_STREAM_CLOSE = EventType.WEBCAM_STREAM_CLOSE
export const DESKTOP_FULLSCREEN_CHANGED = EventType.DESKTOP_FULLSCREEN_CHANGED
